import type { BoardAssetSort, SearchRequest } from './types';

export type BoardAssetsParams = {
  sort: BoardAssetSort;
  includeDescendants: boolean;
  filter: 'all' | 'review';
};

export const keys = {
  me: () => ['me'] as const,
  workspace: (slug: string) => ['workspace', slug] as const,
  libraries: (wsId: string) => ['libraries', wsId] as const,
  boardTree: (wsId: string) => ['board-tree', wsId] as const,
  board: (id: string) => ['board', id] as const,
  boardChildren: (id: string) => ['board-children', id] as const,
  libraryBoards: (libraryId: string) => ['library-boards', libraryId] as const,
  boardAssets: (id: string, params: BoardAssetsParams) => ['board-assets', id, params] as const,
  boardAssetsAll: (id: string) => ['board-assets', id] as const,
  asset: (id: string) => ['asset', id] as const,
  versions: (assetId: string) => ['versions', assetId] as const,
  comments: (assetId: string) => ['comments', assetId] as const,
  spriteIndex: (versionId: string) => ['sprite-index', versionId] as const,
  waveform: (versionId: string) => ['waveform', versionId] as const,
  transcript: (versionId: string) => ['transcript', versionId] as const,
  regions: (versionId: string) => ['regions', versionId] as const,
  processing: (versionId: string) => ['processing', versionId] as const,
  search: (request: SearchRequest) => ['search', request] as const,
  suggestions: (wsId: string, q: string) => ['suggestions', wsId, q] as const,
  savedSearches: (wsId: string) => ['saved-searches', wsId] as const,
  members: (wsId: string) => ['members', wsId] as const,
  roles: (wsId: string) => ['roles', wsId] as const,
  tags: (wsId: string) => ['tags', wsId] as const,
  fields: (wsId: string) => ['fields', wsId] as const,
  shareLinks: (wsId: string) => ['share-links', wsId] as const,
  audit: (wsId: string) => ['audit', wsId] as const,
  people: (wsId: string) => ['people', wsId] as const,
  tokens: () => ['tokens'] as const,
  share: (token: string) => ['share', token] as const,
  shareBoardAssets: (token: string, boardId: string) => ['share-board-assets', token, boardId] as const,
  shareAsset: (token: string, assetId: string) => ['share-asset', token, assetId] as const,
  shareComments: (token: string, assetId: string) => ['share-comments', token, assetId] as const,
  guestForm: (token: string) => ['guest-form', token] as const,
  activity: (assetId: string) => ['activity', assetId] as const,
  // --- release1/C ---
  duplicates: (wsId: string, params: DuplicatesParams) => ['duplicates', wsId, params] as const,
  duplicatesAll: (wsId: string) => ['duplicates', wsId] as const,
  aiTagReview: (wsId: string, params: AiTagReviewParams) => ['ai-tag-review', wsId, params] as const,
  aiTagReviewAll: (wsId: string) => ['ai-tag-review', wsId] as const,
  assetLookup: (wsId: string, sha256: string) => ['asset-lookup', wsId, sha256] as const,
  // --- release1/A ---
  /** same `['board-assets', id, …]` prefix as `boardAssets` so card patches in lib/ws/apply.ts reach filtered pages */
  boardAssetsR1: (id: string, params: BoardAssetsParamsR1) => ['board-assets', id, params] as const,
  boardViews: (boardId: string) => ['board-views', boardId] as const,
  // --- release1/B ---
  pins: (wsId: string) => ['pins', wsId] as const,
  trash: (wsId: string, params: TrashParams) => ['trash', wsId, params] as const,
  trashAll: (wsId: string) => ['trash', wsId] as const,
  /** same prefixes as `libraryBoards` / `boardChildren` so the existing invalidations in lib/ws/apply.ts reach them */
  libraryBoardsR1: (libraryId: string, includeArchived: boolean) => ['library-boards', libraryId, { includeArchived }] as const,
  boardChildrenR1: (id: string, includeArchived: boolean) => ['board-children', id, { includeArchived }] as const,
  assetBoards: (assetId: string) => ['asset-boards', assetId] as const,
  // --- release2/A ---
  systemBoards: (wsId: string) => ['system-boards', wsId] as const,
  systemBoardAssets: (wsId: string, key: string, params: SystemBoardAssetsParams) => ['system-board-assets', wsId, key, params] as const,
  systemBoardAssetsAll: (wsId: string) => ['system-board-assets', wsId] as const,
  // --- release2/B ---
  fieldPolicies: (boardId: string) => ['field-policies', boardId] as const,
  tagGroups: (wsId: string) => ['tag-groups', wsId] as const,
  tagPolicy: (wsId: string) => ['tag-policy', wsId] as const,
  // --- release2/C ---
  rightsPolicy: (wsId: string) => ['rights-policy', wsId] as const,
  boardRetention: (boardId: string) => ['board-retention', boardId] as const,
  // --- release3/D ---
  boardGuests: (boardId: string) => ['board-guests', boardId] as const,
  // --- release3/A ---
  notifications: (wsId: string, params: NotificationsParams) => ['notifications', wsId, params] as const,
  notificationsAll: (wsId: string) => ['notifications', wsId] as const,
  notificationPrefs: (wsId: string) => ['notification-prefs', wsId] as const,
  boardActivity: (boardId: string, kinds: BoardActivityScopeParam) => ['board-activity', boardId, kinds] as const,
  boardActivityAll: (boardId: string) => ['board-activity', boardId] as const,
  // --- release3/B ---
  similar: (assetId: string, params: SimilarParams) => ['similar', assetId, params] as const,
  boardDefaults: (boardId: string) => ['board-defaults', boardId] as const,
  // --- release3/C ---
  downloadPresets: (wsId: string) => ['download-presets', wsId] as const,
};

// --- release1/C ---
export type DuplicatesParams = { mode: 'exact' | 'near'; threshold: number; includeDismissed: boolean; boardId?: string | null };
export type AiTagReviewParams = { boardId?: string | null };

// --- release1/A ---
/** `filters` is the compact `BoardFilters` JSON (undefined = legacy listing); `facets` asks for counts on page 1. */
export type BoardAssetsParamsR1 = BoardAssetsParams & { filters?: string; facets?: boolean; includeArchived?: boolean };

// --- release1/B ---
export type TrashParams = { kind: 'all' | 'board' | 'asset' };

// --- release2/A ---
/** `filters` is the compact caller `BoardFilters` JSON (undefined = none); smart board pages reuse `BoardAssetsParamsR1`. */
export type SystemBoardAssetsParams = { sort: BoardAssetSort; filters?: string; facets?: boolean; includeArchived?: boolean };

// --- release3/A ---
export type NotificationsParams = { kinds?: string[]; unread?: boolean };
export type BoardActivityScopeParam = 'comments' | 'activity' | 'all';

// --- release3/B ---
/** `listSimilarAssets` query: `board_ids` as the comma-separated string the API takes. */
export type SimilarParams = { limit?: number; board_ids?: string; include_descendants?: boolean };
